/**
 * Scoped Quiz Logic Engine Framework - Decoupled Production Instance
 * Infused with Dynamic Client-Side HTML Hydration Deck Matrix
 */
class QuizThemeEngine {
  constructor(element) {
    if (!element) return;

    this.instanceId = element.dataset.sectionId;
    this.customerId = element.dataset.customerId;
    this.storageKey = `quiz_state_${this.instanceId}_${this.customerId}`;
    this.wrapper = element;
    this.enableDiscount = this.wrapper.dataset.enableDiscount === 'true';
    this.discountPercentage = parseFloat(this.wrapper.dataset.discountPercentage);

    this.state = {
      currentIndex: 0,
      totalQuestionSteps: this.wrapper.querySelectorAll('.quiz-step-panel').length - 1,
      isAnimating: false,
      compiledPayload: {
        suggestedProducts: [],
        winningResource: 'generic_default_guide',
        storyFragments: [],
      },
    };

    // Currency Formatting Data Scraped from wrapper markup
    this.currencySymbol = this.wrapper.dataset.currencySymbol || '$';
    this.discountCode = this.wrapper.dataset.discountCode || 'TEACHER15';

    // Component Dom Selections
    this.panels = Array.from(element.querySelectorAll('.quiz-step-panel'));
    this.btnPrev = element.querySelector('.quiz-btn-prev');
    this.btnNext = element.querySelector('.quiz-btn-next');
    this.progressBar = element.querySelector('.quiz-progress-bar');
    this.resultsContainer = element.querySelector(`#QuizResultsView-${this.instanceId}`);
    this.resetBtn = element.querySelector(`#QuizReset-${this.instanceId}`);

    this.loadingScreen = element.querySelector('.quiz-loading-screen');
    this.resultsView = element.querySelector('.quiz-dynamic-results-view');

    this.storyTarget = element.querySelector(`#QuizStoryTarget-${this.instanceId}`);

    // Flexible Dynamic Injection Layout Targets
    this.productsGrid =
      element.querySelector(`#QuizProductsGrid-${this.instanceId}`) ||
      element.querySelector(`#QuizProductsTarget-${this.instanceId}`);
    this.resourceTarget =
      element.querySelector(`#QuizResourceTarget-${this.instanceId}`) ||
      element.querySelector(`#QuizResourceBtn-${this.instanceId}`);

    // Bundle Checkout Targets (Sticky Sidebar & Sticky Bottom Mobile)
    this.mobileStickyBar = element.querySelector(`#MobileStickyCheckoutBar-${this.instanceId}`);

    this.init();
  }

  init() {
    // 1. Attempt to restore previous state
    const savedState = localStorage.getItem(this.storageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        this.state.currentIndex = parsed.currentIndex;

        // Restore selections across interactive input nodes
        parsed.selections.forEach((s) => {
          const input = this.wrapper.querySelector(`[name="${s.name}"][value="${s.value}"]`);
          if (input) {
            input.checked = true;
          }
        });

        // Render the restored step
        this.renderStateView();

        // If they were at the results, re-calculate and jump to results instantly
        if (this.state.currentIndex >= this.state.totalQuestionSteps) {
          this.executeScoringAlgorithm();
          this.processLeadPipeline(); // Decoupled trigger to populate dynamic views
        }
      } catch (e) {
        console.error('Failed to restore quiz state', e);
      }
    } else {
      this.renderStateView();
    }

    this.registerEventBindings();
  }

  registerEventBindings() {
    this.btnNext?.addEventListener('click', () => this.triggerTransition(1));
    this.btnPrev?.addEventListener('click', () => this.triggerTransition(-1));

    // Custom Inter-Module Events Route Switchboard Matrix
    this.wrapper.addEventListener('quiz:next', () => this.triggerTransition(1));
    this.wrapper.addEventListener('change', (e) => this.evaluateInteractiveProgression(e));
    this.resetBtn?.addEventListener('click', () => this.resetQuiz());
  }

  resetQuiz() {
    if (
      confirm('Are you sure you want to restart your quiz? Your current progress will be cleared.')
    ) {
      localStorage.removeItem(this.storageKey);
      window.location.reload(); // Hard refresh to reset state to defaults
    }
  }

  evaluateInteractiveProgression(e) {
    // Streamlined auto-advance progression ONLY for Single-Choice Radio UX interactions
    if (e.target.classList.contains('quiz-interactive-input') && e.target.type === 'radio') {
      setTimeout(() => {
        if (this.state.currentIndex < this.state.totalQuestionSteps - 1) {
          this.triggerTransition(1);
        }
      }, 350);
    }
  }

  triggerTransition(direction) {
    if (this.state.isAnimating) return;
    const nextIndexTarget = this.state.currentIndex + direction;

    if (nextIndexTarget < 0 || nextIndexTarget > this.state.totalQuestionSteps) return;

    this.state.isAnimating = true;
    const currentPanel = this.panels[this.state.currentIndex];
    this.wrapper.setAttribute('data-transition-direction', direction > 0 ? 'next' : 'prev');
    currentPanel.classList.add('is-leaving');
    currentPanel.classList.remove('is-active');

    setTimeout(() => {
      currentPanel.classList.remove('is-leaving');
      this.state.currentIndex = nextIndexTarget;
      this.saveState();
      this.renderStateView();

      // Implicit Execution Gate Hook validation criteria checking
      if (this.state.currentIndex === this.state.totalQuestionSteps) {
        this.executeScoringAlgorithm();
        this.processLeadPipeline();
      }

      this.state.isAnimating = false;
    }, 350);
  }

  renderStateView() {
    this.panels.forEach((panel, stepIndex) => {
      const isTargetActive = stepIndex === this.state.currentIndex;
      panel.classList.toggle('is-active', isTargetActive);
      panel.setAttribute('aria-hidden', (!isTargetActive).toString());
    });

    if (window.innerWidth < 768) {
      this.wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Update Layout Tracking Interfaces
    const currentProgressPercent = (this.state.currentIndex / this.state.totalQuestionSteps) * 100;
    if (this.progressBar) {
      this.progressBar.style.width = `${currentProgressPercent}%`;
    }

    // Dynamic Navigation Visibility Adjustments
    if (this.state.currentIndex === this.state.totalQuestionSteps) {
      if (this.btnPrev) this.btnPrev.style.display = 'none';
      if (this.btnNext) this.btnNext.style.display = 'none';
    } else {
      if (this.btnPrev) {
        this.btnPrev.style.display = 'inline-block';
        this.btnPrev.disabled = this.state.currentIndex === 0;
      }
      if (this.btnNext) {
        this.btnNext.style.display = 'inline-block';
      }
    }

    if (this.resetBtn) {
      this.resetBtn.style.display = this.state.currentIndex > 0 ? 'inline-block' : 'none';
    }

    // Toggle Sticky Mobile Bottom Bar depending on active step
    if (this.mobileStickyBar) {
      if (
        this.state.currentIndex === this.state.totalQuestionSteps &&
        (!this.loadingScreen || this.loadingScreen.style.display === 'none')
      ) {
        this.mobileStickyBar.classList.add('is-active');
      } else {
        this.mobileStickyBar.classList.remove('is-active');
      }
    }
  }

  executeScoringAlgorithm() {
    const catalogFrequencyTally = {};
    const assetVoteMatrix = {};
    const collectedStories = [];
    let parsedOptionsCount = 0;

    // Collate Option Card Element Selections (both Radio and Checkbox nodes)
    this.wrapper.querySelectorAll('.quiz-interactive-input:checked').forEach((input) => {
      parsedOptionsCount++;
      this.tallyIndividualOptionNode(
        input.value,
        input.dataset.productsPayload,
        catalogFrequencyTally,
        assetVoteMatrix,
        input.dataset.optionId,
        collectedStories
      );
    });

    // Fallback Verification Guard
    if (parsedOptionsCount === 0) {
      this.state.compiledPayload = {
        suggestedProducts: [],
        winningResource: 'generic_default_guide',
        storyFragments: [],
      };
      return;
    }

    // Rank catalog matching identifiers based on maximum occurrences descending
    const sortedProductMatches = Object.keys(catalogFrequencyTally).sort((itemA, itemB) => {
      return catalogFrequencyTally[itemB] - catalogFrequencyTally[itemA];
    });

    // Run plurality voting execution to discover leading resource ID asset allocations
    let finalLeaderAssetId = 'generic_default_guide';
    let maximumCalculatedWeight = 0;

    Object.entries(assetVoteMatrix).forEach(([resourceId, currentVotesCount]) => {
      if (currentVotesCount > maximumCalculatedWeight) {
        maximumCalculatedWeight = currentVotesCount;
        finalLeaderAssetId = resourceId;
      }
    });

    this.state.compiledPayload = {
      suggestedProducts: sortedProductMatches,
      winningResource: finalLeaderAssetId,
      storyFragments: collectedStories,
    };
  }

  tallyIndividualOptionNode(
    resourceId,
    productsRawPayload,
    catalogTally,
    assetMatrix,
    optionId,
    collectedStories
  ) {
    if (optionId) {
      collectedStories.push(optionId);
    }

    if (resourceId && assetMatrix) {
      assetMatrix[resourceId] = (assetMatrix[resourceId] || 0) + 1;
    }

    try {
      const formattedCollectionIds = JSON.parse(productsRawPayload || '[]');
      formattedCollectionIds.forEach((id) => {
        if (!id) return;
        catalogTally[id] = (catalogTally[id] || 0) + 1;
      });
    } catch (err) {
      console.error('Payload parsing failure formatting dataset references:', err);
    }
  }

  processLeadPipeline() {
    if (this.resultsContainer) this.resultsContainer.style.display = 'block';

    if (this.loadingScreen && this.resultsView) {
      // Hide the initial layouts gracefully during calculation routines
      this.resultsView.classList.remove('run-unveil');
      this.resultsView.style.opacity = '0';
      this.resultsView.style.pointerEvents = 'none';
      this.loadingScreen.style.display = 'flex';
      this.loadingScreen.style.opacity = '1';

      const loaderText = this.loadingScreen.querySelector('.quiz-loader-text');
      const computationPhases = [
        'Analyzing your response variables...',
        'Cross-checking pedagogical profiles...',
        'Configuring custom classroom resources...',
      ];
      let analyticalIndex = 0;

      // Stagger programmatic textual transitions to communicate high-end personalized parsing
      const statusTickerInterval = setInterval(() => {
        analyticalIndex++;
        if (loaderText && computationPhases[analyticalIndex]) {
          loaderText.innerText = computationPhases[analyticalIndex];
        }
      }, 750);

      // 2. Program completion transition window
      setTimeout(() => {
        clearInterval(statusTickerInterval);

        // Smoothly fade out the loader instead of dropping it brutally
        this.loadingScreen.style.transition = 'opacity 0.35s ease';
        this.loadingScreen.style.opacity = '0';

        setTimeout(() => {
          this.loadingScreen.style.display = 'none';

          // Hydrate dynamic layout elements
          this.populateClientLayoutResults();

          // 3. Unveil contents and pass target hook to fire CSS keyframes
          this.resultsView.style.opacity = '1';
          this.resultsView.style.pointerEvents = 'auto';
          this.resultsView.classList.add('run-unveil');

          if (this.mobileStickyBar) {
            this.mobileStickyBar.classList.add('is-active');
          }

          if (window.innerWidth < 768) {
            this.wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 350); // Matches the loader fade out duration
      }, 2250);
    } else {
      this.populateClientLayoutResults();
    }
  }

  populateClientLayoutResults() {
    // Dynamic color accents for multiple Insight Cards matching the centered full-width design
    const cardColors = [
      {
        border: '#00a896',
        bg: 'var(--page-color-indigo)',
        badgeBg: 'rgba(0,168,150,0.15)',
        emoji: '🧩',
      },
      {
        border: '#ff4d80',
        bg: 'var(--page-color-indigo)',
        badgeBg: 'rgba(255,77,128,0.15)',
        emoji: '📝',
      },
      {
        border: '#3b82f6',
        bg: 'var(--page-color-indigo)',
        badgeBg: 'rgba(59,130,246,0.15)',
        emoji: '✍️',
      },
      {
        border: '#ff7e67',
        bg: 'var(--page-color-indigo)',
        badgeBg: 'rgba(255,126,103,0.15)',
        emoji: '🌟',
      },
    ];

    const carouselWrapper = this.wrapper.querySelector(`#QuizCarouselWrapper-${this.instanceId}`);
    const carouselControls = this.wrapper.querySelectorAll('[data-quiz-carousel-control]');
    const dotsContainer = this.wrapper.querySelector('.quiz-carousel-dots');

    if (this.storyTarget) {
      this.storyTarget.innerHTML = '';
      const rawOptionIds = this.state.compiledPayload.storyFragments || [];

      const optionIds = rawOptionIds.filter((optionId) => {
        return !!this.wrapper.querySelector(`.pool-stories [data-story-option-id="${optionId}"]`);
      });

      if (optionIds.length > 0) {
        if (carouselWrapper) carouselWrapper.classList.remove('hidden');

        optionIds.forEach((optionId, idx) => {
          const sourceStoryNode = this.wrapper.querySelector(
            `.pool-stories [data-story-option-id="${optionId}"]`
          );
          if (sourceStoryNode) {
            const clonedCard = sourceStoryNode.cloneNode(true);

            // Dynamically assign styling parameters on pre-rendered Liquid containers
            const colorTheme = cardColors[idx % cardColors.length];
            const block = clonedCard.querySelector('.quiz-story-narrative-block');
            if (block) {
              block.style.setProperty('--card-accent-color', colorTheme.border);
              block.style.setProperty('--card-badge-bg', colorTheme.badgeBg);
              block.style.backgroundColor = colorTheme.bg;

              const emojiEl = block.querySelector('.quiz-insight-emoji');
              if (emojiEl) emojiEl.innerText = colorTheme.emoji;
            }

            // Cleanly support transition states
            clonedCard.classList.add('hydration-card-story');
            if (idx === 0) {
              clonedCard.classList.add('is-active');
            }

            this.storyTarget.appendChild(clonedCard);
          }
        });

        // Render Pagination dots declaratively
        if (dotsContainer) {
          dotsContainer.innerHTML = '';
          if (optionIds.length > 1) {
            optionIds.forEach((_, dIdx) => {
              const dot = document.createElement('span');
              dot.className = 'quiz-carousel-dot';
              if (dIdx === 0) dot.classList.add('is-active');
              dotsContainer.appendChild(dot);
            });
          }
        }

        // Active Slider Controls
        if (optionIds.length > 1) {
          if (carouselControls)
            carouselControls.forEach((control) => (control.style.display = 'flex'));

          let activeSlideIndex = 0;
          const slides = this.storyTarget.querySelectorAll('.hydration-card-story');
          const dots = dotsContainer.querySelectorAll('.quiz-carousel-dot');

          const updateSliderUI = (targetIdx) => {
            if (targetIdx < 0) targetIdx = slides.length - 1;
            if (targetIdx >= slides.length) targetIdx = 0;
            activeSlideIndex = targetIdx;

            slides.forEach((slide, sIdx) => {
              if (sIdx === activeSlideIndex) {
                slide.classList.add('is-active');
              } else {
                slide.classList.remove('is-active');
              }
            });

            dots.forEach((dot, dIdx) => {
              if (dIdx === activeSlideIndex) {
                dot.classList.add('is-active');
              } else {
                dot.classList.remove('is-active');
              }
            });
          };

          // Re-bind listeners cleanly without memory-leak overlaps
          const prevBtn = this.wrapper.querySelector('.quiz-carousel-btn-prev');
          const nextBtn = this.wrapper.querySelector('.quiz-carousel-btn-next');

          if (prevBtn) {
            const newPrev = prevBtn.cloneNode(true);
            prevBtn.parentNode.replaceChild(newPrev, prevBtn);
            newPrev.addEventListener('click', () => updateSliderUI(activeSlideIndex - 1));
          }
          if (nextBtn) {
            const newNext = nextBtn.cloneNode(true);
            nextBtn.parentNode.replaceChild(newNext, nextBtn);
            newNext.addEventListener('click', () => updateSliderUI(activeSlideIndex + 1));
          }

          dots.forEach((dot, targetIdx) => {
            dot.addEventListener('click', () => updateSliderUI(targetIdx));
          });

          updateSliderUI(0);
        } else {
          if (carouselControls)
            carouselControls.forEach((control) => (control.style.display = 'none'));
        }
      } else {
        if (carouselWrapper) carouselWrapper.classList.add('hidden');
      }
    }

    // 1. DYNAMIC RESOURCE HYDRATION PIPELINE
    if (this.resourceTarget && this.state.compiledPayload.winningResource) {
      const targetPoolCard = this.wrapper.querySelector(
        `.pool-resources [data-resource-id="${this.state.compiledPayload.winningResource}"]`
      );

      if (targetPoolCard) {
        this.resourceTarget.innerHTML = '';
        const clonedResourceCard = targetPoolCard.cloneNode(true);
        this.resourceTarget.appendChild(clonedResourceCard);

        const inlineLeadForm = clonedResourceCard.querySelector('.quiz-hydration-lead-form');
        if (inlineLeadForm) {
          inlineLeadForm.addEventListener('submit', (e) =>
            this.handleInlineLeadSubmit(e, inlineLeadForm)
          );
        }
      } else {
        this.resourceTarget.innerHTML =
          '<p class="quiz-empty-message">Your custom strategy resource profile is being generated.</p>';
      }
    }

    // 2. DYNAMIC CATALOG CHANNELS GRID HYDRATION
    if (!this.productsGrid) return;
    this.productsGrid.innerHTML = '';

    const displayTopTierLimitSlice = this.state.compiledPayload.suggestedProducts.slice(0, 4);
    const activeVariantsArray = [];
    let totalPriceCents = 0;

    if (displayTopTierLimitSlice.length === 0) {
      this.productsGrid.innerHTML =
        '<p class="quiz-empty-message">Discover our general catalog selections for optimized recommendations!</p>';
    } else {
      displayTopTierLimitSlice.forEach((productIdString, outputIndex) => {
        const sourceProductNode = this.wrapper.querySelector(
          `.pool-products [data-product-id="${productIdString}"]`
        );

        if (sourceProductNode) {
          const clonedProductCard = sourceProductNode.cloneNode(true);

          const targetItemNode = clonedProductCard.querySelector('.quiz-product-row-item');
          const productVariantId = targetItemNode ? targetItemNode.dataset.variantId : null;
          const productPriceCents = targetItemNode ? targetItemNode.dataset.productPrice : null;

          if (productPriceCents) {
            totalPriceCents += parseInt(productPriceCents, 10);
          }

          if (productVariantId) {
            activeVariantsArray.push(`${productVariantId}:1`);
          }

          const indexPlaceholder = clonedProductCard.querySelector(
            '.quiz-product-rank-placeholder'
          );
          if (indexPlaceholder) {
            indexPlaceholder.innerHTML = `<span class="quiz-badge-circle font-index-${outputIndex}">#${
              outputIndex + 1
            }</span>`;
          }

          const individualAtcBtn = clonedProductCard.querySelector('.quiz-btn-add-to-cart');
          if (individualAtcBtn && productVariantId) {
            individualAtcBtn.addEventListener('click', () =>
              this.handleIndividualAddToCart(productVariantId, individualAtcBtn)
            );
          }

          this.productsGrid.appendChild(clonedProductCard);
        }
      });
    }

    // 3. AUTO CALCULATION OF THE 15% OFF SAVINGS VALUES
    const formattedSubtotal = (totalPriceCents / 100).toFixed(2);
    const discountMultiplier = this.discountPercentage / 100;
    const discountCents = this.enableDiscount
      ? Math.round(totalPriceCents * discountMultiplier)
      : 0;
    const formattedDiscount = (discountCents / 100).toFixed(2);
    const finalTotalCents = totalPriceCents - discountCents;
    const formattedFinalTotal = (finalTotalCents / 100).toFixed(2);

    // Update Desktop Pricing Displays
    const oldPriceEl = this.wrapper.querySelector(`#SidebarOldTotal-${this.instanceId}`);
    const discountPriceEl = this.wrapper.querySelector(`#SidebarDiscount-${this.instanceId}`);
    const finalPriceEl = this.wrapper.querySelector(`#SidebarFinalTotal-${this.instanceId}`);

    if (oldPriceEl) oldPriceEl.innerText = `${this.currencySymbol}${formattedSubtotal}`;
    if (discountPriceEl) discountPriceEl.innerText = `-${this.currencySymbol}${formattedDiscount}`;
    if (finalPriceEl) finalPriceEl.innerText = `${this.currencySymbol}${formattedFinalTotal}`;

    // Update Mobile Sticky Checkout Pricing Displays
    const mobileOldPriceEl = this.wrapper.querySelector(`#MobileOldTotal-${this.instanceId}`);
    const mobileFinalPriceEl = this.wrapper.querySelector(`#MobileFinalTotal-${this.instanceId}`);

    if (mobileOldPriceEl) mobileOldPriceEl.innerText = `${this.currencySymbol}${formattedSubtotal}`;
    if (mobileFinalPriceEl)
      mobileFinalPriceEl.innerText = `${this.currencySymbol}${formattedFinalTotal}`;

    // Direct redirection parameters applying checkout links
    const handleBuyAllRedirection = () => {
      if (activeVariantsArray.length > 0) {
        window.location.href = `/cart/${activeVariantsArray.join(',')}?discount=${
          this.discountCode
        }`;
      }
    };

    const buyAllButtonElement = this.wrapper.querySelector(`#QuizBuyAll-${this.instanceId}`);
    if (buyAllButtonElement) {
      if (activeVariantsArray.length > 0) {
        buyAllButtonElement.style.display = 'block';
        buyAllButtonElement.onclick = handleBuyAllRedirection;
      } else {
        buyAllButtonElement.style.display = 'none';
      }
    }

    const buyAllMobileButtonElement = this.wrapper.querySelector(
      `#QuizBuyAllMobile-${this.instanceId}`
    );
    if (buyAllMobileButtonElement && activeVariantsArray.length > 0) {
      buyAllMobileButtonElement.onclick = handleBuyAllRedirection;
    }
  }

  handleIndividualAddToCart(variantId, buttonElement) {
    if (!variantId) return;

    buttonElement.disabled = true;
    buttonElement.innerText = 'ADDING...';

    const cartRoutePrefix = window.Shopify?.routes?.root || '/';

    fetch(`${cartRoutePrefix}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart API post failure mapping data payloads');
        return response.json();
      })
      .then(() => {
        // High fidelity checkmark success states
        buttonElement.innerText = '✓ ADDED! 🎉';
        buttonElement.style.backgroundColor = 'var(--page-accent-primary)';

        document.dispatchEvent(
          new CustomEvent('updatecart', { detail: { variant_id: variantId } })
        );

        setTimeout(() => {
          buttonElement.disabled = false;
          buttonElement.innerText = 'ADD TO CART';
          buttonElement.style.backgroundColor = '';
        }, 2000);
      })
      .catch((err) => {
        console.error('Error executing checkout cart processing action:', err);
        buttonElement.innerText = 'RETRY';
        buttonElement.disabled = false;
      });
  }

  handleInlineLeadSubmit(e, formElement) {
    e.preventDefault();
    const emailField = formElement.querySelector('.quiz-lead-email-input');
    const submitBtn = formElement.querySelector('.quiz-btn-submit-gate');

    const targetKlaviyoListId = this.wrapper.dataset.klaviyoList || '';
    const quizTitle = formElement.dataset.quizTitle;

    if (!emailField || !emailField.value.trim()) return;

    if (submitBtn) {
      submitBtn.classList.add('is-loading');
      submitBtn.innerText = 'Unlocking...';
      submitBtn.disabled = true;
    }

    const quizSelections = {};

    this.wrapper.querySelectorAll('.quiz-interactive-input:checked').forEach((input) => {
      const questionText = input.dataset.questionTitle || input.name || 'Unknown Question';
      const answerText = input.dataset.answerTitle || input.value || 'Unknown Answer';

      if (quizSelections[questionText]) {
        quizSelections[questionText] += `, ${answerText}`;
      } else {
        quizSelections[questionText] = answerText;
      }
    });

    // Handle standard Klaviyo tracking push
    try {
      if (window.klaviyo) {
        klaviyo.identify({
          email: emailField.value.trim(),
        });

        klaviyo.track('Submitted Quiz Recommendation Guide', {
          Quiz: quizTitle,
          CalculatedProducts: this.state.compiledPayload.suggestedProducts,
          ResourceIdentifier: this.state.compiledPayload.winningResource,
          KlaviyoListContext: targetKlaviyoListId,
          QuizResponses: quizSelections,
        });

        if (targetKlaviyoListId) {
          window.bitcube.subscribeToList(targetKlaviyoListId, emailField.value.trim());
        }
      }
    } catch (err) {
      console.warn('Klaviyo script tracking integration loaded with exceptions: ', err);
    }

    setTimeout(() => {
      localStorage.removeItem(this.storageKey);

      // Declarative success display toggle (No dynamic innerHTML generation)
      const gateContainer = formElement.closest('.quiz-resource-lead-gate');
      const successState = gateContainer?.querySelector('.quiz-lead-success-state');
      const formFieldsWrapper = gateContainer?.querySelector('.quiz-lead-form-fields-wrapper');

      if (successState) {
        if (formFieldsWrapper) formFieldsWrapper.style.display = 'none';
        successState.style.display = 'block';
      }
    }, 600);
  }

  saveState() {
    const selections = [];
    this.wrapper.querySelectorAll('.quiz-interactive-input:checked').forEach((el) => {
      selections.push({ name: el.name, value: el.value });
    });

    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        currentIndex: this.state.currentIndex,
        selections: selections,
      })
    );
  }
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.quiz-engine-wrapper').forEach((quizElementInstance) => {
    new QuizThemeEngine(quizElementInstance);
  });
});
